import React from 'react';
import { List, ListItem, ListItemText } from '@material-ui/core';
import { reduce } from 'lodash/fp';
import DateRangeIcon from '@material-ui/icons/DateRange';
import WatchLaterIcon from '@material-ui/icons/WatchLater';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom';
import NoMeetingRoomIcon from '@material-ui/icons/NoMeetingRoom';
import { useTranslation } from 'react-i18next';
import { usePriceService } from '../../../../utils/hooks/priceService';
import { FullHeightBox, ListItemIconDense, SmallAvatar, SquareCard, TableIcon } from '../../../../ui-kit';
import { getFullName, getInitials } from '../../../../utils';
import { useDateTimeService } from '../../../../utils/hooks/dateTimeService';
import { useUser } from '../../../../utils/hooks/auth';
import { BillAmount } from '../../../billManagement/components/Bill/BillAmount';
import { BillState, OrderState } from '../../../../generated/graphql';
import { OrderDetails } from './types';

interface SidebarProps {
    order: OrderDetails;
}

const OrderSidebar = ({ order }: SidebarProps) => {
    const { priceService } = usePriceService();
    const { t } = useTranslation();
    const { dateTimeService } = useDateTimeService();
    const { user } = useUser();

    const {
        openedBy: { account: { firstName, lastName, imageUrl } },
        closedBy,
        createdUtc,
        orderState,
        bills,
        place,
        orderLines,
    } = order;

    const pendingAmount = reduce((acc, { price }) => (acc + price), 0, orderLines);
    const dateString = dateTimeService.localizeDate(createdUtc, user.cultureCode);
    const timeString = dateTimeService.localizeTime(createdUtc, user.cultureCode);

    const amountPaid = reduce((acc, { total, dicountedTotalValue, roundedPrice, billState }) => {
        if (billState === BillState.Voided) {
            return acc;
        }

        const discountedPrice = roundedPrice || dicountedTotalValue || total;
        return {
            original: priceService.add(total, acc.original),
            discounted: priceService.add(discountedPrice, acc.discounted)
        };
    },
    { original: 0, discounted: 0 },
    bills
    );

    const fullNameOpenedBy = getFullName({ firstName, lastName });
    const initialsOpenedBy = getInitials({ firstName, lastName });
    const placeName = place ? place.name : t('Take away');
    const localizedOrderState = t(orderState).toLowerCase();

    const hasNoBills = bills.length === 0;
    const billAmountProps = {
        total: hasNoBills ? pendingAmount : amountPaid.original,
        dicountedTotalValue: hasNoBills ? undefined : amountPaid.discounted,
    };

    return (
        <FullHeightBox component={SquareCard}>
            <List dense>
                <ListItem>
                    <ListItemIconDense>
                        <DateRangeIcon />
                    </ListItemIconDense>
                    <ListItemText primary={dateString} />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        <WatchLaterIcon />
                    </ListItemIconDense>
                    <ListItemText primary={timeString} />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        <MonetizationOnIcon />
                    </ListItemIconDense>
                    <ListItemText
                        primary={(
                            <BillAmount
                                {...billAmountProps}
                            />
                        )}
                    />
                </ListItem>
                <ListItem>
                    <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary={t('Opened by')} />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        <SmallAvatar src={imageUrl || undefined}>{initialsOpenedBy}</SmallAvatar>
                    </ListItemIconDense>
                    <ListItemText primary={fullNameOpenedBy} />
                </ListItem>
                {closedBy && (
                    <>
                        <ListItem>
                            <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary={t('Closed by')} />
                        </ListItem>
                        <ListItem>
                            <ListItemIconDense>
                                <SmallAvatar
                                    src={closedBy.account.imageUrl || undefined}
                                >
                                    {getInitials(closedBy.account)}
                                </SmallAvatar>
                            </ListItemIconDense>
                            <ListItemText primary={getFullName(closedBy.account)} />
                        </ListItem>
                    </>
                )}
                <ListItem>
                    <ListItemIconDense>
                        <TableIcon />
                    </ListItemIconDense>
                    <ListItemText primary={placeName} />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        {orderState === OrderState.Opened ?
                            <MeetingRoomIcon /> :
                            <NoMeetingRoomIcon />}
                    </ListItemIconDense>
                    <ListItemText primary={t('Order is {{orderState}}', { orderState: localizedOrderState })} />
                </ListItem>
            </List>
        </FullHeightBox>
    );
};


export default OrderSidebar;
